import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { fetchAddresses } from "../store";
import "./OrderSummary.css";

const lockIconProps = { size: 16, strokeWidth: 1.75 };

export default function OrderSummary({ onClose }) {
  const userId = useSelector((state) => state.user.userId); // Fetch userId from store
  const addresses = useSelector((state) => state.addresses.items);
  const addressesLoading = useSelector((state) => state.addresses.loading);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get default address or first address as initial selection
  const defaultAddress =
    addresses.find((addr) => addr.isDefault) || addresses[0];
  const [selectedAddress, setSelectedAddress] = useState(
    defaultAddress?._id || null
  );
  const cartItems = useSelector((state) => {
    const prods = state.products?.items || [];
    const items = state.cart?.items || [];
    return items
      .map((cartItem) => {
        const product = prods.find((p) => p._id === cartItem.product);
        if (!product) return null;
        return { ...cartItem, product };
      })
      .filter(Boolean);
  });

  const DEFAULT_TAX_PCT = 18;
  const taxPctForProduct = (product) => {
    const n = Number(product?.taxPercentage);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : DEFAULT_TAX_PCT;
  };

  const getSubtotal = () =>
    cartItems.reduce(
      (sum, item) =>
        sum +
        (item.product.price?.amount
          ? Number(item.product.price.amount) * item.quantity
          : 0),
      0
    );
  const getTaxTotal = () =>
    +cartItems
      .reduce((sum, item) => {
        const line =
          item.product.price?.amount != null
            ? Number(item.product.price.amount) * item.quantity
            : 0;
        return sum + line * (taxPctForProduct(item.product) / 100);
      }, 0)
      .toFixed(2);
  const getGrandTotal = () =>
    +(Number(getSubtotal()) + Number(getTaxTotal())).toFixed(2);

  const getItemTotal = (item) =>
    item.product.price?.amount
      ? Number(item.product.price.amount) * item.quantity
      : 0;

  const getItemCount = () => cartItems.reduce((count, item) => count + 1, 0);

  const HandlePaymentRequest = async (e) => {
    e.stopPropagation();

    if (!addresses.length) {
      alert(
        "Add at least one delivery address in your profile before placing an order."
      );
      return;
    }

    // Get selected shipping address
    const shippingAddress = addresses.find(
      (addr) => addr._id === selectedAddress
    );

    if (!shippingAddress) {
      alert("Please select a shipping address");
      return;
    }

    navigate("/payment", {
      state: {
        amount: getGrandTotal().toFixed(2),
        currency: "INR",
        receipt: "rcptid_123",
        userId: userId,
        shippingAddress: shippingAddress,
        billingAddress: shippingAddress, // For simplicity, using shipping as billing
        paymentMethod: "CreditCard",
        successRoute: "/order-success",
        errorRoute: "/payment-error",
      },
    });

  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchAddresses(userId));
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (addresses.length > 0 && !selectedAddress) {
      setSelectedAddress(
        addresses.find((addr) => addr.isDefault)?._id || addresses[0]._id
      );
    }
  }, [addresses, selectedAddress]);

  const hasDeliveryAddress = addresses.length > 0;
  const canProceedToPayment =
    hasDeliveryAddress && !addressesLoading && Boolean(selectedAddress);

  const getDeliveryAddressBlock = () => {
    if (addressesLoading) {
      return (
        <p className="os-address-loading">Loading your addresses…</p>
      );
    }
    if (hasDeliveryAddress) {
      return (
        <>
          <div className="os-address-dropdown-container">
            <select
              className="os-address-dropdown"
              id="addressSelect"
              value={selectedAddress || ""}
              onChange={(e) => setSelectedAddress(e.target.value)}
            >
              {addresses.map((addr) => (
                <option key={addr._id} value={addr._id}>
                  {addr.addressName} - {addr.fullName}, {addr.addressLine1},{" "}
                  {addr.city}, {addr.state} {addr.pincode}
                </option>
              ))}
            </select>
          </div>

          <div className="os-selected-address" id="selectedAddressDetails">
            {addresses
              .filter((addr) => addr._id === selectedAddress)
              .map((addr) => (
                <div className="os-address-details" key={addr._id}>
                  <div className="os-address-field">
                    <span className="os-field-label">Full Name</span>
                    <span className="os-field-value">{addr.fullName}</span>
                  </div>
                  <div className="os-address-field">
                    <span className="os-field-label">Phone</span>
                    <span className="os-field-value">{addr.phone}</span>
                  </div>
                  <div className="os-address-field">
                    <span className="os-field-label">Street Address</span>
                    <span className="os-field-value">{addr.addressLine1}</span>
                  </div>
                  <div className="os-address-field">
                    <span className="os-field-label">City</span>
                    <span className="os-field-value">{addr.city}</span>
                  </div>
                  <div className="os-address-field">
                    <span className="os-field-label">State</span>
                    <span className="os-field-value">{addr.state}</span>
                  </div>
                  <div className="os-address-field">
                    <span className="os-field-label">ZIP Code</span>
                    <span className="os-field-value">{addr.pincode}</span>
                  </div>
                </div>
              ))}
          </div>
        </>
      );
    }
    return (
      <div className="os-address-empty">
        <p className="os-address-empty-message">
          You need at least one saved delivery address to complete your order.
          Add an address in your profile, then return here to continue checkout.
        </p>
        <button
          type="button"
          className="os-add-address-btn"
          onClick={() =>
            navigate("/user-profile", {
              state: { activeSection: "addresses" },
            })
          }
        >
          Add delivery address
        </button>
      </div>
    );
  };

  return (
    <div className="os-main-container">
      <nav className="os-breadcrumb">
        <a href="/" className="os-breadcrumb-item">
          Home
        </a>
        <span className="os-breadcrumb-separator">›</span>
        <a href="/cartPage" className="os-breadcrumb-item">
          Cart
        </a>
        <span className="os-breadcrumb-separator">›</span>
        <span className="os-breadcrumb-item active">Order Summary</span>
      </nav>

      <div className="os-page-header">
        <h1 className="os-page-title">Order Summary</h1>
        <p className="os-page-subtitle">
          Review your order details and delivery address
        </p>
      </div>

      <div className="os-checkout-container">
        <div className="os-checkout-main">
          <div className="os-section-card">
            <h2 className="os-section-title">
              Delivery Address
            </h2>

            {getDeliveryAddressBlock()}
          </div>

          <div className="os-section-card">
            <h2 className="os-section-title">
              Order Items
            </h2>

            <div className="os-order-items">
              {cartItems.map((item) => (
                <div className="os-order-item" key={item.product._id}>
                  <div className="os-item-image">
                    <img
                      src={item.product.images?.[0]}
                      alt={item.product.name}
                      style={{ width: 48, height: 48, objectFit: "contain" }}
                    />
                  </div>
                  <div className="os-item-details">
                    <div className="os-item-name">{item.product.name}</div>
                    <div className="os-item-specs">
                      {item.product.bagSize?.cm} •{" "}
                      {item.product.specifications?.gsm}g GSM • BF:{" "}
                      {item.product.specifications?.bf}
                    </div>
                  </div>
                  <div className="os-item-quantity">Qty: {item.quantity}</div>
                  <div className="os-item-price">
                    ₹{getItemTotal(item).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="os-order-summary">
          <h3 className="os-summary-title">Order Summary</h3>

          <div className="os-summary-row">
            <span className="os-summary-label">
              Subtotal ({getItemCount()} items)
            </span>
            <span className="os-summary-value">₹{getSubtotal().toFixed(2)}</span>
          </div>

          <div className="summary-row">
            <span className="os-summary-label">Tax (per product)</span>
            <span className="os-summary-value">
              ₹{Number(getTaxTotal()).toFixed(2)}
            </span>
          </div>

          <div className="os-summary-row summary-total">
            <span className="os-total-label">Total</span>
            <span className="os-total-value">₹{getGrandTotal().toFixed(2)}</span>
          </div>

          <button
            className="os-payment-btn"
            type="button"
            disabled={!canProceedToPayment}
            onClick={HandlePaymentRequest}
          >
            Proceed to Payment
          </button>

          <button className="os-back-to-cart" onClick={onClose}>
            Back to Cart
          </button>

          <div className="os-secure-checkout">
            <span className="os-secure-checkout-icon" aria-hidden>
              <Lock {...lockIconProps} />
            </span>
            <span>Secure Checkout</span>
          </div>
        </div>
      </div>
    </div>
  );
}
